<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <component
      :is="cardType"
      v-bind="{ ...propTypes }"
      :class="{'tile-editor': isEditMode, themeClass}"
      class="content-list-tile"
    >
      <ImageCard>
        <template #image>
          <div class="content-list-tile-image">
            <sc-image v-if="isEditMode" :media="imageField"/>
            <ShImage v-else class="img" :src="setImageSrc" :alt="imageAlt"/>
          </div>
        </template>
        <template #title>
          <heading
            v-if="hasTitle"
            class="content-list-tile-title"
            :tagField="headingSize"
            :textField="titleField"
            desktop-size="size-5"
            mobile-size="size-5"
          />
          <div v-else/>
        </template>
        <template #description>
          <sc-text v-if="hasDescription" class="content-list-tile-description" :field="descriptionField"/>
        </template>
        <template #default>
          <sc-link v-if="isEditMode" :field="linkField"/>
          <sh-button
            v-if="linkHref && !isTile && !isEditMode"
            :naked="isNaked"
            class="link"
            role="button"
            :color="buttonColor"
            :class="linkColor"
          >
            {{ linkText }}
          </sh-button>
        </template>
      </ImageCard>
    </component>
  </sc-render>
</template>

<script>
import { Image, Text, Link } from '@sitecore-jss/sitecore-jss-vue'
import { ImageCard, ShImage } from '@sohohouse/design-system'
import { createNamespacedHelpers } from 'vuex'

import CTA from '~/constants/appearance'
import ShLink from '~/components/sh-link'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'

import ScRender from '../sc-render'
import Heading from '../heading'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  name: 'ContentListTile',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    ScImage: Image,
    ScRender,
    ScText: Text,
    ScLink: Link,
    ShLink,
    ImageCard,
    ShImage,
    Heading
  },

  computed: {
    ...mapJssGetters(['isEditMode', 'colorTheme']),
    ...mapEnvGetters(['isDarkTheme']),
    ...mapFields([
      'title',
      'description',
      'link',
      'image'
    ]),
    linkHref () {
      return get(this.linkField, 'value.href', '')
    },
    linkType () {
      return get(this.linkField, 'value.linktype', '')
    },
    linkTarget () {
      return get(this.linkField, 'value.target', '')
    },
    imageAlt () {
      return get(this.imageField, 'value.alt')
    },
    hasDescription () {
      return this.isEditMode || get(this.descriptionField, 'value', false)
    },
    hasTitle () {
      return this.isEditMode || get(this.titleField, 'value', false)
    },
    isNaked () {
      return this.fields.ctaAppearance && this.fields.ctaAppearance.value !== CTA.BUTTON
    },
    linkText () {
      return this.fields.link.value.text
    },
    isTile () {
      return this.fields.ctaAppearance && this.fields.ctaAppearance.value === 'Tile'
    },
    setImageSrc () {
      return this.imageField.value.src
    },
    themeClass () {
      return `theme-${this.colorTheme}`.toLowerCase()
    },
    buttonColor () {
      return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
    },
    linkColor () {
      return this.isDarkTheme && this.isNaked ? 'naked-link' : ''
    },
    cardType () {
      if (!this.linkHref || this.isEditMode) return 'div'

      return 'sh-link'
    },
    propTypes () {
      if (this.cardType === 'div') {
        return { tabindex: '0' }
      }

      return {
        to: this.linkHref,
        target: this.linkTarget
      }
    },
    headingSize () {
      return this.$parent.tileHeadingSize
    }
  }
}
</script>

<style lang="scss" scoped>

.tile-editor {
  margin-right: $space-050;
  min-width: 200px;
}

a {
  text-decoration: none;
}

.content-list-tile > div {
  @include tablet {
    width: 100%;
    min-width: 100%;
    max-width: 0;
  }
}

.content-list-tile > div {
  margin-bottom: $space-080;
    @include tablet {
      margin-bottom: 0;
    }
}

.theme-black {
  .naked-link {
    color: $color-white;
  }

  .content-list-link ::v-deep > a {
    color: $color-white;
  }

  .subscription-input {
    input {
      color: $color-white;
    }

    .input-label.focused ::v-deep {
      background: none;
      color: $color-white;
    }

    .input-label ::v-deep label {
      color: $color-white;
      left: 0;
    }
  }
}
</style>
